// src/pages/JournalistDashboard.js - Journalist Dashboard
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import Navbar from '../components/common/Navbar';
import InvestigationList from '../components/investigations/InvestigationList';
import InvestigationForm from '../components/investigations/InvestigationForm';
import InvestigationDetail from '../components/investigations/InvestigationDetail';
import AlertList from '../components/alerts/AlertList';
import ReportSummarizer from '../components/reports/ReportSummarizer';
import ReportsList from '../components/reports/ReportsList';
import ReportForm from '../components/reports/ReportForm';
import StatsCards from '../components/analytics/StatsCards';
import TrendChart from '../components/analytics/TrendChart';
import CategoryChart from '../components/analytics/CategoryChart';
import GeographicChart from '../components/analytics/GeographicChart';
import ExportButton from '../components/export/ExportButton';
import DisclaimerFooter from '../components/common/DisclaimerFooter';

const JournalistDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();

  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    totalInvestigations: 0,
    activeInvestigations: 0,
    unreadAlerts: 0,
    pendingReports: 0,
  });
  const [loading, setLoading] = useState(true);
  const [showInvestigationForm, setShowInvestigationForm] = useState(false);
  const [selectedInvestigation, setSelectedInvestigation] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAddReportModal, setShowAddReportModal] = useState(false);
  const [selectedReportForInvestigation, setSelectedReportForInvestigation] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [period, setPeriod] = useState('weekly');

  // Use ref to track if we're currently loading to prevent duplicate calls
  const isLoadingRef = useRef(false);
  const lastLoadTimeRef = useRef(0);
  const timeoutRef = useRef(null);
  const DEBOUNCE_MS = 5000; // Minimum 5 seconds between calls

  const loadStats = useCallback(async (force = false) => {
    const now = Date.now();
    // Debounce: don't call if we're already loading or if called too recently
    if (!force && (isLoadingRef.current || (now - lastLoadTimeRef.current < DEBOUNCE_MS))) {
      console.log('[JournalistDashboard] loadStats skipped - too soon or already loading');
      return;
    }

    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // For forced calls, execute immediately; otherwise debounce
    const executeLoad = async () => {
      isLoadingRef.current = true;
      lastLoadTimeRef.current = Date.now();

    try {
      // Silent background refresh - don't set loading state
      // Get investigations count
      const investigationsRes = await api.get('/api/journalist/investigations?limit=1000');
      const investigations = investigationsRes.data.investigations || [];
      
      // Get unread alerts count
      const alertsRes = await api.get('/api/journalist/alerts/unread-count');
      const unreadCount = alertsRes.data.unread_count || 0;
      
      // Force a refresh of the alert list if it's visible
      // This ensures the notification count stays in sync
      
      // Get pending reports (approximate)
      const reportsRes = await api.get('/api/reports/reports?status=pending&limit=1&offset=0');
      const pendingReports =
        typeof reportsRes.data.total === 'number'
          ? reportsRes.data.total
          : reportsRes.data.count ?? 0;

      // Only update if values actually changed to prevent unnecessary re-renders
      setStats(prevStats => {
        const newStats = {
          totalInvestigations: investigations.length,
          activeInvestigations: investigations.filter(inv => inv.status === 'active').length,
          unreadAlerts: unreadCount,
          pendingReports: pendingReports,
        };
        // Only update if something changed
        if (
          prevStats.totalInvestigations !== newStats.totalInvestigations ||
          prevStats.activeInvestigations !== newStats.activeInvestigations ||
          prevStats.unreadAlerts !== newStats.unreadAlerts ||
          prevStats.pendingReports !== newStats.pendingReports
        ) {
          return newStats;
        }
        return prevStats;
      });
      } catch (error) {
        console.error('Failed to load stats:', error);
        // Don't show error to user on background refresh
      } finally {
        isLoadingRef.current = false;
      }
    };

    if (force) {
      // Execute immediately for forced calls
      executeLoad();
    } else {
      // Debounce non-forced calls
      timeoutRef.current = setTimeout(executeLoad, 1000);
    }
  }, []);

  useEffect(() => {
    if (!user || (user.role !== 'journalist' && user.role !== 'admin')) {
      navigate('/dashboard');
      return;
    }
    // Initial load with loading state
    const initialLoad = async () => {
      setLoading(true);
      await loadStats(true); // Force initial load
      setLoading(false);
    };
    initialLoad();
    
    // Background refresh every 30 seconds (silent, no loading state)
    const interval = setInterval(() => {
      loadStats(false); // Don't force, respect debounce
    }, 30000);
    return () => clearInterval(interval);
  }, [user, navigate, loadStats]);

  // Separate effect to handle manual refresh triggers
  useEffect(() => {
    if (refreshKey > 0) {
      // Use setTimeout to debounce rapid refreshKey changes
      const timeoutId = setTimeout(() => {
        loadStats(true); // Force refresh on manual trigger
      }, 2000); // 2 second debounce for manual refreshes
      return () => clearTimeout(timeoutId);
    }
  }, [refreshKey, loadStats]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const response = await api.get(`/api/analytics/dashboard?period=${period}`);
      setAnalytics(response.data);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab, period, loadAnalytics]);

  const handleInvestigationCreated = () => {
    setShowInvestigationForm(false);
    setRefreshKey(prev => prev + 1);
    // Don't call loadStats() directly - the refreshKey effect will handle it
  };

  const handleInvestigationSelect = (investigation) => {
    setSelectedInvestigation(investigation);
    setActiveTab('investigations');
  };

  const handleInvestigationClose = () => {
    setSelectedInvestigation(null);
    setRefreshKey(prev => prev + 1);
    // Don't call loadStats() directly - the refreshKey effect will handle it
  };

  if (loading && stats.totalInvestigations === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 flex items-center justify-center transition-colors">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shayek-primary dark:border-shayek-red-light mx-auto mb-4"></div>
          <p className="text-shayek-light-600 dark:text-shayek-dark-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-shayek-light-50 to-shayek-light-100 dark:from-shayek-dark dark:to-shayek-dark-50 transition-colors">
      <Navbar showHome={true} />

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('journalist.stats.totalInvestigations')}</p>
                <p className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mt-1">{stats.totalInvestigations}</p>
              </div>
              <div className="w-12 h-12 bg-shayek-primary/10 dark:bg-shayek-primary/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-shayek-primary dark:text-shayek-red-light" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('journalist.stats.activeInvestigations')}</p>
                <p className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mt-1">{stats.activeInvestigations}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('journalist.stats.unreadAlerts')}</p>
                <p className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mt-1">{stats.unreadAlerts}</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center relative">
                {stats.unreadAlerts > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 dark:bg-red-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                    {stats.unreadAlerts > 9 ? '9+' : stats.unreadAlerts}
                  </span>
                )}
                <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600">{t('journalist.stats.pendingReports')}</p>
                <p className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mt-1">{stats.pendingReports}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek mb-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
          <div className="border-b border-shayek-light-300 dark:border-shayek-dark-200 overflow-x-auto">
            <nav className="flex -mb-px flex-nowrap min-w-0">
              <button
                onClick={() => {
                  setActiveTab('overview');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'overview'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t('journalist.tabs.overview')}
              </button>
              <button
                onClick={() => {
                  setActiveTab('investigations');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'investigations'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t('journalist.tabs.investigations')}
              </button>
              <button
                onClick={() => {
                  setActiveTab('alerts');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors relative ${
                  activeTab === 'alerts'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t('journalist.tabs.alerts')}
                {stats.unreadAlerts > 0 && (
                  <span className="ml-2 px-2 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {stats.unreadAlerts > 9 ? '9+' : stats.unreadAlerts}
                  </span>
                )}
              </button>
              <button
                onClick={() => {
                  setActiveTab('summarize');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'summarize'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t('journalist.tabs.summarize')}
              </button>
              <button
                onClick={() => {
                  setActiveTab('analytics');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'analytics'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className="mr-2">📊</span>
                {t('journalist.tabs.analytics') || 'Analytics'}
              </button>
              <button
                onClick={() => {
                  setActiveTab('reports');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'reports'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t('journalist.tabs.reports')}
              </button>
              <button
                onClick={() => {
                  setActiveTab('new-report');
                  setSelectedInvestigation(null);
                }}
                className={`flex-shrink-0 px-4 sm:px-6 py-4 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === 'new-report'
                    ? 'border-shayek-primary dark:border-shayek-red-light text-shayek-primary dark:text-shayek-red-light'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {t('journalist.tabs.newReport') || 'New Report'}
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h2 className="text-xl font-bold text-shayek-light-900 dark:text-shayek-dark-900">{t('journalist.overview.title')}</h2>
                  <button
                    onClick={() => setShowInvestigationForm(true)}
                    className="px-4 py-2 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors"
                  >
                    {t('journalist.overview.newInvestigation')}
                  </button>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-gray-50 dark:bg-shayek-dark-50 rounded-lg p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900 mb-4">{t('journalist.overview.recentInvestigations')}</h3>
                    <InvestigationList
                      limit={5}
                      onSelect={handleInvestigationSelect}
                      refreshKey={refreshKey}
                    />
                  </div>
                  <div className="bg-gray-50 dark:bg-shayek-dark-50 rounded-lg p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-shayek-dark-900 mb-4">{t('journalist.overview.recentAlerts')}</h3>
                    <AlertList 
                      limit={5} 
                      refreshKey={refreshKey}
                      onAlertsUpdated={() => {
                        // Force stats refresh when alerts are updated
                        loadStats(true);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'investigations' && (
              <div>
                {selectedInvestigation ? (
                  <InvestigationDetail
                    investigationId={selectedInvestigation.id}
                    onClose={handleInvestigationClose}
                    refreshKey={refreshKey}
                  />
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-bold text-shayek-light-900 dark:text-shayek-dark-900">{t('journalist.tabs.investigations')}</h2>
                      <button
                        onClick={() => setShowInvestigationForm(true)}
                        className="px-4 py-2 bg-shayek-primary dark:bg-shayek-red-light text-white rounded-lg hover:bg-shayek-primary-600 dark:hover:bg-shayek-red-dark transition-colors"
                      >
                        {t('journalist.investigations.new')}
                      </button>
                    </div>
                    <InvestigationList
                      onSelect={handleInvestigationSelect}
                      refreshKey={refreshKey}
                    />
                  </div>
                )}
              </div>
            )}

            {activeTab === 'alerts' && (
              <div>
                <h2 className="text-xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">{t('journalist.tabs.alerts')}</h2>
                <AlertList 
                  refreshKey={refreshKey}
                  onAlertsUpdated={() => {
                    // Force stats refresh when alerts are updated
                    loadStats(true);
                  }}
                />
              </div>
            )}

            {activeTab === 'summarize' && (
              <div>
                <h2 className="text-xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">{t('journalist.tabs.summarize')}</h2>
                <ReportSummarizer />
              </div>
            )}

            {activeTab === 'analytics' && (
              <div className="space-y-6">
                <StatsCards
                  statistics={analytics?.statistics}
                  period={period}
                  onPeriodChange={setPeriod}
                  loading={analyticsLoading}
                />
                <div className="flex justify-end">
                  <ExportButton type="analytics" period={period} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Report Trends</h3>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
                    ) : (
                      <TrendChart data={analytics?.trends || []} period={period} />
                    )}
                  </div>
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Category Breakdown</h3>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
                    ) : (
                      <CategoryChart data={analytics?.category_breakdown || []} />
                    )}
                  </div>
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 lg:col-span-2 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Geographic Distribution</h3>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
                    ) : (
                      <GeographicChart data={analytics?.geographic_distribution || []} />
                    )}
                  </div>
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Violation Type</h3>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
                    ) : (
                      <CategoryChart data={(analytics?.violation_type_breakdown || []).map((r) => ({ category: r.type, count: r.count }))} />
                    )}
                  </div>
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Affected Type</h3>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
                    ) : (
                      <CategoryChart data={(analytics?.affected_type_breakdown || []).map((r) => ({ category: r.type, count: r.count }))} />
                    )}
                  </div>
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Affected Category</h3>
                    {analyticsLoading ? (
                      <div className="flex items-center justify-center h-64 text-shayek-light-500 dark:text-shayek-dark-500">Loading...</div>
                    ) : (
                      <CategoryChart data={analytics?.affected_category_breakdown || []} />
                    )}
                  </div>
                </div>
                {analytics?.recent_patterns && analytics.recent_patterns.length > 0 && (
                  <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek p-6 border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
                    <h3 className="text-lg font-semibold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">Recent Patterns</h3>
                    <div className="space-y-2">
                      {analytics.recent_patterns.slice(0, 5).map((pattern) => (
                        <div
                          key={pattern.id}
                          className="p-4 bg-shayek-light-50 dark:bg-shayek-dark-50 rounded-lg border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors"
                        >
                          <div className="flex justify-between items-start">
                            <div>
                              <span className="text-sm font-medium text-shayek-light-900 dark:text-shayek-dark-900">
                                {pattern.pattern_type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                              </span>
                              <p className="text-sm text-shayek-light-600 dark:text-shayek-dark-600 mt-1">
                                Detected {new Date(pattern.detected_at).toLocaleString()}
                              </p>
                            </div>
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              pattern.severity === 'high' || pattern.severity === 'critical'
                                ? 'bg-red-100 text-red-800'
                                : pattern.severity === 'medium'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-shayek-light-100 dark:bg-shayek-dark-100 text-shayek-light-800 dark:text-shayek-dark-800'
                            }`}>
                              {pattern.severity}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'reports' && (
              <div>
                <h2 className="text-xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-4">{t('journalist.tabs.reports')}</h2>
                <ReportsList
                  onAddToInvestigation={(report) => {
                    setSelectedReportForInvestigation(report);
                    setShowAddReportModal(true);
                  }}
                  refreshKey={refreshKey}
                />
              </div>
            )}

            {activeTab === 'new-report' && (
              <div className="max-w-4xl mx-auto bg-white dark:bg-shayek-dark-100 rounded-xl shadow-shayek p-8 border border-shayek-light-300 dark:border-shayek-dark-200">
                <h2 className="text-2xl font-bold text-shayek-light-900 dark:text-shayek-dark-900 mb-6">
                  {t('journalist.newReport.title') || 'Submit Professional Report'}
                </h2>
                <ReportForm 
                  onSuccess={() => {
                    setRefreshKey(prev => prev + 1);
                    setActiveTab('reports');
                  }} 
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Report to Investigation Modal */}
      {showAddReportModal && selectedReportForInvestigation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-shayek-lg max-w-2xl w-full border border-shayek-light-300 dark:border-shayek-dark-200 transition-colors">
            <div className="p-6 border-b border-gray-200 dark:border-shayek-dark-200">
              <h2 className="text-xl font-bold text-shayek-light-900 dark:text-shayek-dark-900">{t('journalist.reports.addToInvestigation')}</h2>
            </div>
            <div className="p-6">
              <p className="text-gray-700 dark:text-shayek-dark-600 mb-4">{t('journalist.reports.selectInvestigation')}</p>
              <InvestigationList
                onSelect={async (investigation) => {
                  try {
                    await api.post(`/api/journalist/investigations/${investigation.id}/reports`, {
                      report_id: selectedReportForInvestigation.id,
                    });
                    setShowAddReportModal(false);
                    setSelectedReportForInvestigation(null);
                    setRefreshKey(prev => prev + 1);
                    loadStats();
                  } catch (error) {
                    console.error('Failed to add report to investigation:', error);
                  }
                }}
                refreshKey={refreshKey}
              />
              <div className="mt-4 flex gap-4">
                <button
                  onClick={() => {
                    setShowAddReportModal(false);
                    setSelectedReportForInvestigation(null);
                  }}
                  className="px-6 py-2 bg-gray-200 dark:bg-shayek-dark-200 text-gray-700 dark:text-shayek-dark-300 rounded-lg hover:bg-gray-300 dark:hover:bg-shayek-dark-300 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Investigation Form Modal */}
      {showInvestigationForm && (
        <InvestigationForm
          onClose={() => setShowInvestigationForm(false)}
          onSuccess={handleInvestigationCreated}
        />
      )}
      <DisclaimerFooter />
    </div>
  );
};

export default JournalistDashboard;

